/**
 * Course Detail Page
 * Displays full course content with comprehensive learning features:
 * - Bilingual subtitles with audio sync
 * - Sentence-by-sentence display with grammar points
 * - Comprehensive vocabulary with definitions, examples, synonyms
 * - VocabularyModal for detailed word info
 * - Individual sentence audio playback
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  coursesService,
  Course,
  FullCourseData,
  ComprehensiveVocabularyEntry,
  AnnotatedSentence,
  Section,
} from '../services/coursesService';
import type { PhraseEntry } from '../types/creator-workflow';
import { getSegmentTranslation, getTranslation } from '../utils/getTranslation';
import { learningService } from '../services/learningService';
import { cefrKnowledgeService, WordAnnotation } from '../services/cefrKnowledgeService';
import type { CourseProgressSummary, ModeProgressSummary } from '../types/learning';
import UnifiedAudioPlayer from '../components/UnifiedAudioPlayer';
import type { SubtitleCue } from '../utils/srtParser';
import SentenceDisplay from '../components/creator-workflow/SentenceDisplay';
import SentenceEditModal from '../components/creator-workflow/SentenceEditModal';
import PhraseEditModal from '../components/creator-workflow/PhraseEditModal';
import VocabularyModal from '../components/creator-workflow/VocabularyModal';
import { creatorWorkflowService } from '../services/creatorWorkflowService';
import { unenroll, enroll, checkEnrollment } from '../services/enrollmentService';
import { convertPhonetic, getUserPhoneticSystem } from '../utils/phoneticConverter';
import { useAuth } from '../contexts/AuthContext';
import './CourseDetailPage.css';
import './CourseDetailEpisodes.css';
import { BilingualTitle } from '../components/common/BilingualTitle';
import { TagChipInput } from '../components/common/TagChipInput';

import { resolveMediaUrl } from '../utils/mediaUrl';
import { useSeoTags, buildCourseJsonLd } from '../hooks/useSeoTags';

// Episode Card Grid Component (Style D) for multi-episode courses
const EPISODE_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const formatEpDuration = (seconds?: number) => {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const getEpSpeakers = (segments: any[]): string[] => {
  const speakers = new Set<string>();
  segments?.forEach(seg => {
    if (seg.speakerName) speakers.add(seg.speakerName);
  });
  return Array.from(speakers);
};

// Full card grid — shown on episode selection page (no ep selected)
const EpisodeCardGrid: React.FC<{
  sections: Section[];
  courseId: string;
  navigate: (path: string) => void;
}> = ({ sections, courseId, navigate }) => {
  const { t } = useTranslation(['course']);
  const episodes = sections
    .flatMap(s => s.episodes || [])
    .sort((a, b) => a.orderIndex - b.orderIndex);

  if (episodes.length <= 1) return null;

  return (
    <div className="episode-card-grid">
      <div className="episode-card-grid__cards">
        {episodes.map((ep, i) => {
          const color = EPISODE_COLORS[i % EPISODE_COLORS.length];
          const speakers = getEpSpeakers(ep.segments);
          const duration = formatEpDuration(ep.durationSeconds);
          const segCount = ep.segments?.length || 0;

          return (
            <button
              key={ep.id}
              className="episode-card"
              style={{ '--episode-color': color } as React.CSSProperties}
              onClick={() => navigate(`/courses/${courseId}?ep=${ep.orderIndex}`)}
            >
              <div className="episode-card__header">
                <span className="episode-card__number" style={{ background: color }}>
                  {i + 1}
                </span>
                <span className="episode-card__title">{ep.title}</span>
              </div>
              <div className="episode-card__meta">
                {duration && <span>{duration}</span>}
                <span>{segCount} {t('course:detail.sentencesUnit')}</span>
                {speakers.length > 0 && <span>{speakers.join(', ')}</span>}
              </div>
              <div className="episode-card__progress-bar">
                <div className="episode-card__progress-fill" style={{ width: '0%', background: color }} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Compact episode pills — shown when an episode IS selected, for quick switching
const EpisodePillNav: React.FC<{
  sections: Section[];
  courseId: string;
  navigate: (path: string) => void;
  selectedEp: number;
}> = ({ sections, courseId, navigate, selectedEp }) => {
  const episodes = sections
    .flatMap(s => s.episodes || [])
    .sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <div className="episode-pill-nav">
      {episodes.map((ep, i) => {
        const isActive = ep.orderIndex === selectedEp;
        const color = EPISODE_COLORS[i % EPISODE_COLORS.length];
        return (
          <button
            key={ep.id}
            className={`episode-pill ${isActive ? 'episode-pill--active' : ''}`}
            style={{ '--pill-color': color } as React.CSSProperties}
            onClick={() => navigate(`/courses/${courseId}?ep=${ep.orderIndex}`)}
            title={ep.title}
          >
            <span className="episode-pill__num" style={isActive ? { background: color } : undefined}>
              {i + 1}
            </span>
            <span className="episode-pill__title">{ep.title}</span>
          </button>
        );
      })}
    </div>
  );
};

const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation(['course', 'common', 'creator', 'pricing']);

  const userLang = user?.languagePreference || 'zh-TW';
  const [data, setData] = useState<FullCourseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [progressSummary, setProgressSummary] = useState<CourseProgressSummary | null>(null);
  // CEFR annotations by segment ID
  const [annotationsBySegment, setAnnotationsBySegment] = useState<Record<string, WordAnnotation[]>>({});

  // Determine if in creator mode based on browser URL (not ownership)
  // /creator/* paths = creator mode, /courses/* paths = learner mode
  // Use window.location.pathname as React Router's location.pathname may not
  // include the full path prefix in some configurations
  const isCreatorMode = window.location.pathname.startsWith('/creator');

  // Check if user can edit (must be creator mode AND course owner)
  const canEdit = useMemo(() => {
    return isCreatorMode && !!(user && data?.course?.creatorId && user.id === data.course.creatorId);
  }, [isCreatorMode, user, data?.course?.creatorId]);


  // Vocabulary modal
  const [selectedVocab, setSelectedVocab] = useState<ComprehensiveVocabularyEntry | null>(null);

  // Active view tab
  const [activeTab, setActiveTab] = useState<'sentences' | 'phrases' | 'vocabulary' | 'writing'>('sentences');

  // Collapsible sections
  const [learningModesOpen, setLearningModesOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [sentencesCollapseState, setSentencesCollapseState] = useState(true); // true = all collapsed
  const [sentencesCollapseKey, setSentencesCollapseKey] = useState(0); // increment to force re-render

  // Title editing state
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [nativeTitleValue, setNativeTitleValue] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  // Sentence edit modal state
  const [editingSentenceIndex, setEditingSentenceIndex] = useState<number | null>(null);
  const [savingSentence, setSavingSentence] = useState(false);

  // Phrase edit modal state
  const [editingPhraseIndex, setEditingPhraseIndex] = useState<number | null>(null);
  const [savingPhrase, setSavingPhrase] = useState(false);

  // Thumbnail generation state
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [showThumbnailConfirm, setShowThumbnailConfirm] = useState(false);

  // Course management state
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [showPrivateConfirm, setShowPrivateConfirm] = useState(false);
  const [showPublicConfirm, setShowPublicConfirm] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [permissionDropdownOpen, setPermissionDropdownOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingPermission, setUpdatingPermission] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const permissionDropdownRef = useRef<HTMLDivElement>(null);

  // Whitelist state
  const [whitelistOpen, setWhitelistOpen] = useState(false);
  const [whitelistEntries, setWhitelistEntries] = useState<{ id: string; email: string; createdAt: string }[]>([]);
  const [whitelistInput, setWhitelistInput] = useState('');
  const [whitelistLoading, setWhitelistLoading] = useState(false);

  // Metadata editing state (accent, contentType, cefrLevel, tags)
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaAccent, setMetaAccent] = useState('');
  const [metaContentType, setMetaContentType] = useState('');
  const [metaCefrLevel, setMetaCefrLevel] = useState('');
  const [metaTags, setMetaTags] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [savingMeta, setSavingMeta] = useState(false);

  // Delete course state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Unenroll state
  const [showUnenrollModal, setShowUnenrollModal] = useState(false);
  const [unenrollConfirmText, setUnenrollConfirmText] = useState('');
  const [isUnenrolling, setIsUnenrolling] = useState(false);

  // Enrollment check state
  const [isEnrolled, setIsEnrolled] = useState<boolean | null>(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [pendingModePath, setPendingModePath] = useState<string | null>(null);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Get user's phonetic system preference
  const phoneticSystem = useMemo(() => getUserPhoneticSystem(), []);

  // Determine native language key (e.g., "zh-TW") — skip if English
  const nativeLang = i18n.language && !i18n.language.startsWith('en') ? i18n.language : '';

  // Close dropdowns on click outside
  useEffect(() => {
    if (!statusDropdownOpen && !permissionDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (statusDropdownOpen && statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
      if (permissionDropdownOpen && permissionDropdownRef.current && !permissionDropdownRef.current.contains(e.target as Node)) {
        setPermissionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen, permissionDropdownOpen]);

  // Load whitelist when course is private
  useEffect(() => {
    if (!courseId || !data?.course || data.course.permission !== 'private' || !canEdit) return;
    const loadWhitelist = async () => {
      setWhitelistLoading(true);
      try {
        const entries = await coursesService.getWhitelist(courseId);
        setWhitelistEntries(entries);
      } catch (err) {
        console.error('Failed to load whitelist:', err);
      } finally {
        setWhitelistLoading(false);
      }
    };
    loadWhitelist();
  }, [courseId, data?.course?.permission, canEdit]);

  // Update titleValue when data loads
  useEffect(() => {
    if (data?.course?.title) {
      setTitleValue(data.course.title);
    }
    if (nativeLang && data?.course?.titles) {
      setNativeTitleValue(data.course.titles[nativeLang] || '');
    }
  }, [data?.course?.title, data?.course?.titles, nativeLang]);

  // Handle title save
  const handleTitleSave = async () => {
    const titleChanged = titleValue.trim() && titleValue !== data?.course?.title;
    const nativeChanged = nativeLang && nativeTitleValue !== (data?.course?.titles?.[nativeLang] || '');
    if (!courseId || (!titleChanged && !nativeChanged)) {
      setEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    try {
      const updates: Partial<Course> = {};
      if (titleChanged) {
        updates.title = titleValue.trim();
      }
      if (nativeChanged) {
        updates.titles = { ...(data?.course?.titles || {}), [nativeLang]: nativeTitleValue.trim() };
      }
      await coursesService.updateCourse(courseId, updates);
      // Reload course data to get updated info
      await loadCourseData();
      setEditingTitle(false);
      setSuccessMessage(t('course:manage.titleUpdated'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save title:', error);
    } finally {
      setSavingTitle(false);
    }
  };

  // Handle title key press
  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTitleValue(data?.course?.title || '');
      setNativeTitleValue(nativeLang ? (data?.course?.titles?.[nativeLang] || '') : '');
      setEditingTitle(false);
    }
  };

  // Metadata editing handlers (accent, contentType, cefrLevel)
  const handleStartEditMeta = () => {
    const course = data?.course;
    setMetaAccent((course as any)?.accent || '');
    setMetaContentType((course as any)?.contentType || '');
    setMetaCefrLevel(course?.cefrLevel || '');
    setMetaTags(course?.tags || []);
    setEditingMeta(true);
    if (tagSuggestions.length === 0) {
      coursesService.listAllTags().then(setTagSuggestions).catch(() => undefined);
    }
  };

  const handleSaveMeta = async () => {
    if (!courseId) return;
    setSavingMeta(true);
    try {
      const updates: any = { tags: metaTags };
      if (metaCefrLevel) updates.cefrLevel = metaCefrLevel;
      if (metaContentType) updates.contentType = metaContentType;
      if (metaAccent) updates.accent = metaAccent;
      await coursesService.updateCourse(courseId, updates);
      // Update local state
      if (data) {
        setData({
          ...data,
          course: {
            ...data.course,
            cefrLevel: metaCefrLevel || data.course.cefrLevel,
            tags: metaTags,
            ...(metaContentType ? { contentType: metaContentType } : {}),
            ...(metaAccent ? { accent: metaAccent } : {}),
          } as any,
        });
      }
      setEditingMeta(false);
      setSuccessMessage(t('course:manage.metaUpdated'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save metadata:', error);
    } finally {
      setSavingMeta(false);
    }
  };

  // Handle sentence edit save
  const handleSentenceSave = async (index: number, updates: Partial<AnnotatedSentence>) => {
    if (!data?.sourceDraftId) {
      throw new Error('Source draft ID not available for editing');
    }
    setSavingSentence(true);
    try {
      await creatorWorkflowService.updateSentence(data.sourceDraftId, index, updates);
      // Reload course data to get updated sentences
      await loadCourseData();
      setSuccessMessage(t('course:manage.sentenceUpdated'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } finally {
      setSavingSentence(false);
    }
  };

  // Handle phrase edit save
  const handlePhraseSave = async (index: number, updates: Partial<PhraseEntry>) => {
    if (!data?.sourceDraftId) {
      throw new Error('Source draft ID not available for editing');
    }
    setSavingPhrase(true);
    try {
      await creatorWorkflowService.updatePhrase(data.sourceDraftId, index, updates);
      // Reload course data to get updated phrases
      await loadCourseData();
      setSuccessMessage(t('course:manage.phraseUpdated'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } finally {
      setSavingPhrase(false);
    }
  };

  // Handle thumbnail generation
  const handleGenerateThumbnail = async () => {
    if (!courseId) return;
    setGeneratingThumbnail(true);
    setThumbnailError(null);
    try {
      await coursesService.generateThumbnail(courseId, { style: 'professional' });
      // Reload course data to get new thumbnail URL
      await loadCourseData();
      setSuccessMessage(t('course:manage.thumbnailGenerated'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
      const errorMessage = error instanceof Error ? error.message : t('course:manage.failedToLoadCourse');
      setThumbnailError(errorMessage);
      setTimeout(() => setThumbnailError(null), 5000);
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  // Handle course deletion
  const handleDeleteCourse = async () => {
    if (!courseId || deleteConfirmText !== 'delete') return;
    setIsDeleting(true);
    try {
      await coursesService.deleteCourse(courseId);
      navigate('/creator/courses');
    } catch (error) {
      console.error('Failed to delete course:', error);
      setIsDeleting(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus: 'published' | 'draft') => {
    if (!courseId || !course) return;
    // Unpublishing needs confirmation
    if (course.status === 'published' && newStatus === 'draft') {
      setShowUnpublishConfirm(true);
      setStatusDropdownOpen(false);
      return;
    }
    setUpdatingStatus(true);
    setStatusDropdownOpen(false);
    try {
      await coursesService.updateCourse(courseId, { status: newStatus });
      await loadCourseData();
      setSuccessMessage(t('course:manage.coursePublished'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Confirm unpublish
  const handleConfirmUnpublish = async () => {
    if (!courseId) return;
    setUpdatingStatus(true);
    setShowUnpublishConfirm(false);
    try {
      await coursesService.updateCourse(courseId, { status: 'draft' });
      await loadCourseData();
      setSuccessMessage(t('course:manage.courseUnpublished'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to unpublish:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle permission change with confirmation
  const handlePermissionChange = (newPerm: 'public' | 'private') => {
    if (!course || course.permission === newPerm) {
      setPermissionDropdownOpen(false);
      return;
    }
    setPermissionDropdownOpen(false);
    if (newPerm === 'private') {
      setShowPrivateConfirm(true);
    } else {
      setShowPublicConfirm(true);
    }
  };

  const handleConfirmPermission = async (newPerm: 'public' | 'private') => {
    if (!courseId) return;
    setUpdatingPermission(true);
    setShowPrivateConfirm(false);
    setShowPublicConfirm(false);
    try {
      await coursesService.updateCourse(courseId, { permission: newPerm });
      await loadCourseData();
      setSuccessMessage(t(newPerm === 'public' ? 'course:manage.coursePublic' : 'course:manage.coursePrivate'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to update permission:', err);
    } finally {
      setUpdatingPermission(false);
    }
  };

  // Whitelist management
  const handleAddWhitelistEmails = async () => {
    if (!courseId || !whitelistInput.trim()) return;
    const emails = whitelistInput.split(/[,\n]/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) return;
    try {
      const result = await coursesService.addToWhitelist(courseId, emails);
      if (result.added.length > 0) {
        const entries = await coursesService.getWhitelist(courseId);
        setWhitelistEntries(entries);
      }
      setWhitelistInput('');
      setSuccessMessage(t('course:manage.whitelistAdded', { count: result.added.length }));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to add whitelist emails:', err);
    }
  };

  const handleRemoveWhitelistEmail = async (email: string) => {
    if (!courseId) return;
    try {
      await coursesService.removeFromWhitelist(courseId, email);
      setWhitelistEntries(prev => prev.filter(e => e.email !== email));
    } catch (err) {
      console.error('Failed to remove whitelist email:', err);
    }
  };

  // Handle unenroll
  const handleUnenroll = async () => {
    if (!courseId || unenrollConfirmText !== 'cancel') return;
    setIsUnenrolling(true);
    try {
      await unenroll(courseId);
      navigate('/courses');
    } catch (error) {
      console.error('Failed to unenroll:', error);
      setIsUnenrolling(false);
    }
  };

  // Handle learning mode click — always verify enrollment via API
  const handleModeClick = async (e: React.MouseEvent, path: string) => {
    // Creator mode — always allow
    if (isCreatorMode) return;
    // Not logged in — redirect to login
    if (!user) {
      e.preventDefault();
      navigate(`/login?redirect=${encodeURIComponent(path)}`);
      return;
    }
    // Always prevent default first, then check enrollment via API
    e.preventDefault();
    try {
      const status = await checkEnrollment(courseId!);
      setIsEnrolled(status.isEnrolled);
      if (status.isEnrolled) {
        navigate(path);
      } else {
        setPendingModePath(path);
        setShowEnrollModal(true);
      }
    } catch {
      // API failed — fallback to local state
      if (isEnrolled) {
        navigate(path);
      } else {
        setPendingModePath(path);
        setShowEnrollModal(true);
      }
    }
  };

  // Handle enrollment from modal
  const handleEnroll = async () => {
    if (!courseId) return;
    setIsEnrolling(true);
    try {
      await enroll(courseId);
      setIsEnrolled(true);
      setShowEnrollModal(false);
      // Navigate to the pending learning mode
      if (pendingModePath) {
        navigate(pendingModePath);
        setPendingModePath(null);
      }
    } catch (error: any) {
      console.error('Failed to enroll:', error);
      // Check if it's a subscription-required error (403 with SUBSCRIPTION_REQUIRED code)
      const errorCode = error?.response?.data?.error?.code || error?.error?.code;
      if (error?.response?.status === 403 || errorCode === 'SUBSCRIPTION_REQUIRED') {
        setShowEnrollModal(false);
        setShowSubscriptionModal(true);
      }
    } finally {
      setIsEnrolling(false);
    }
  };

  const loadCourseData = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError(null);
    try {
      const [courseData, summary, annotationsData, enrollmentStatus] = await Promise.all([
        coursesService.getFullCourse(courseId),
        // Progress is per-user, so skip the auth-required endpoint entirely
        // for anonymous visitors — otherwise the global 401 interceptor
        // hard-redirects them away from a page that's supposed to be public
        // (and Googlebot's JS pass gets bounced too, killing SEO).
        user ? learningService.getProgressSummary(courseId).catch(() => null) : Promise.resolve(null),
        cefrKnowledgeService.getCourseAnnotations(courseId).catch(() => ({ annotations: {}, stats: { totalAnnotations: 0, segmentsWithAnnotations: 0 } })),
        user ? checkEnrollment(courseId).catch(() => ({ isEnrolled: false })) : Promise.resolve({ isEnrolled: false }),
      ]);
      setData(courseData);
      setProgressSummary(summary);
      setAnnotationsBySegment(annotationsData.annotations);
      setIsEnrolled(enrollmentStatus.isEnrolled);
    } catch (err: unknown) {
      console.error('Failed to load course:', err);
      if (err instanceof Error) {
        // Handle axios error structure
        const axiosError = err as Error & { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || err.message);
      } else {
        setError(t('course:manage.failedToLoadCourse'));
      }
    } finally {
      setLoading(false);
    }
  }, [courseId, t, user]);

  useEffect(() => {
    if (courseId) {
      loadCourseData();
    }
  }, [courseId, loadCourseData]);


  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // Helper to render progress badge for learning modes
  const renderProgressBadge = (progress: ModeProgressSummary | undefined) => {
    if (!progress) return null;

    const { completedCount, totalCount, isCompleted, bestScore } = progress;

    return (
      <div className="mode-progress">
        <span className="progress-count">
          {completedCount}/{totalCount}
          {isCompleted && <span className="completed-badge">✓</span>}
        </span>
        {bestScore !== undefined && bestScore > 0 && (
          <span className="best-score">
            {t('course:manage.best', { score: Math.round(bestScore) })}
          </span>
        )}
      </div>
    );
  };

  // Build mapping from sentence index to database segment ID
  // Segments are flattened from sections/episodes in order
  // Must be called before early returns (React hooks rules)
  const segmentIdsByIndex = useMemo((): string[] => {
    if (!data?.sections) return [];
    const ids: string[] = [];
    for (const section of data.sections) {
      for (const episode of section.episodes || []) {
        for (const segment of episode.segments || []) {
          ids.push(segment.id);
        }
      }
    }
    return ids;
  }, [data?.sections]);

  // Episode filtering — must be before early returns (React hooks rules)
  const epParam = searchParams.get('ep');
  const allEpisodes = useMemo(() => {
    if (!data?.sections) return [];
    return data.sections.flatMap(s => s.episodes || []).sort((a, b) => a.orderIndex - b.orderIndex);
  }, [data?.sections]);
  const isMultiEpisode = allEpisodes.length > 1;
  const selectedEp = isMultiEpisode && epParam !== null ? parseInt(epParam, 10) : null;
  const selectedEpisode = selectedEp !== null ? allEpisodes[selectedEp] : null;

  // Filter content by selected episode
  const filteredContent = useMemo(() => {
    const allSentences = data?.sentences;
    const allComprehensiveVocabulary = data?.comprehensiveVocabulary;
    const allPhrases = data?.phrases;

    if (selectedEp === null || !allEpisodes.length || !data) {
      return {
        sentences: allSentences,
        comprehensiveVocabulary: allComprehensiveVocabulary,
        phrases: allPhrases,
        audioStartTime: undefined as number | undefined,
        audioEndTime: undefined as number | undefined,
      };
    }

    // Calculate start/end index for the selected episode
    let startIdx = 0;
    let count = 0;
    let episodeSegments: any[] = [];
    for (const ep of allEpisodes) {
      const segs = ep.segments || [];
      if (ep.orderIndex === selectedEp) {
        count = segs.length;
        episodeSegments = segs;
        break;
      }
      startIdx += segs.length;
    }

    const filteredSentences = allSentences?.slice(startIdx, startIdx + count);

    // Compute audio time range from episode segments
    let audioStartTime: number | undefined;
    let audioEndTime: number | undefined;
    if (episodeSegments.length > 0) {
      // Sort by startTime to ensure correct range
      const sorted = [...episodeSegments].sort((a, b) => (a.startTime || 0) - (b.startTime || 0));
      audioStartTime = sorted[0]?.startTime;
      audioEndTime = sorted[sorted.length - 1]?.endTime;
    }

    // Filter vocabulary to words that appear in this episode's sentences
    const epTexts = (filteredSentences || []).map((s: any) => (s.text || '').toLowerCase()).join(' ');
    const filteredVocab = allComprehensiveVocabulary?.filter((v: any) =>
      epTexts.includes((v.word || '').toLowerCase())
    );
    const filteredPhrases = allPhrases?.filter((p: any) =>
      epTexts.includes((p.phrase || p.text || '').toLowerCase())
    );

    return {
      sentences: filteredSentences,
      comprehensiveVocabulary: filteredVocab,
      phrases: filteredPhrases,
      audioStartTime,
      audioEndTime,
    };
  }, [selectedEp, allEpisodes, data]);

  // Episode time range — passed to UnifiedAudioPlayer
  const epAudioStart = filteredContent.audioStartTime;
  const epAudioEnd = filteredContent.audioEndTime;

  // Deep-link: ?s=<episodeLocalIndex>&eid=<episodeId> — scroll to and auto-expand.
  // `s` is an episode-local index (matches how notes store `sentenceIndex` in
  // LearningLayout). `eid` resolves to the episode's orderIndex so we can set
  // the `ep` param on multi-episode courses.
  const sParam = searchParams.get('s');
  const eidParam = searchParams.get('eid');
  const targetSentenceIdx = useMemo(() => {
    if (sParam === null) return null;
    const n = parseInt(sParam, 10);
    return Number.isNaN(n) ? null : n;
  }, [sParam]);

  // Auto-redirect eid → ep param so the rest of the page's episode logic works.
  useEffect(() => {
    if (!eidParam || !isMultiEpisode || allEpisodes.length === 0) return;
    const ep = allEpisodes.find(e => e.id === eidParam);
    if (!ep) return;
    if (selectedEp === ep.orderIndex) return;
    const next = new URLSearchParams(searchParams);
    next.set('ep', String(ep.orderIndex));
    next.delete('eid');
    navigate(`/courses/${courseId}?${next.toString()}`, { replace: true });
  }, [eidParam, isMultiEpisode, allEpisodes, selectedEp, searchParams, navigate, courseId]);

  // `s` is already in the local index space of filteredContent.sentences
  // (ep-local when an episode is selected, global when single-episode).
  const targetLocalSentenceIdx = targetSentenceIdx;

  // Fallback: build subtitle cues from sentences when no SRT files available
  const draftSubtitleEn_ = data?.subtitleUrlEn;
  const draftSubtitleNative_ = data?.subtitleUrlNative;
  const filteredSentences = filteredContent.sentences;
  const courseDuration_ = data?.course?.durationSeconds ?? 0;

  const fallbackCuesEn = useMemo<SubtitleCue[]>(() => {
    if (draftSubtitleEn_ || !filteredSentences?.length) return [];
    const texted = filteredSentences.filter((s: any) => s.text);
    if (!texted.length) return [];
    // Check if timing data is valid (not all zeros)
    const hasRealTiming = texted.some((s: any) => Number(s.start_time) > 0 || Number(s.end_time) > 0);
    if (hasRealTiming) {
      return texted.map((s: any, i) => ({
        index: i + 1,
        startTime: Number(s.start_time),
        endTime: Number(s.end_time),
        text: s.text,
      }));
    }
    // No timing data: evenly distribute across course duration
    const dur = courseDuration_ || 60;
    const interval = dur / texted.length;
    return texted.map((s: any, i) => ({
      index: i + 1,
      startTime: i * interval,
      endTime: (i + 1) * interval,
      text: s.text,
    }));
  }, [draftSubtitleEn_, filteredSentences, courseDuration_]);

  const fallbackCuesNative = useMemo<SubtitleCue[]>(() => {
    if (draftSubtitleNative_ || !filteredSentences?.length) return [];
    const texted = filteredSentences.filter((s: any) => s.translation || getSegmentTranslation(s, userLang));
    if (!texted.length) return [];
    const hasRealTiming = texted.some((s: any) => Number(s.start_time) > 0 || Number(s.end_time) > 0);
    if (hasRealTiming) {
      return texted.map((s: any, i) => ({
        index: i + 1,
        startTime: Number(s.start_time),
        endTime: Number(s.end_time),
        text: s.translation || getSegmentTranslation(s, userLang) || '',
      }));
    }
    const dur = courseDuration_ || 60;
    const interval = dur / texted.length;
    return texted.map((s: any, i) => ({
      index: i + 1,
      startTime: i * interval,
      endTime: (i + 1) * interval,
      text: s.translation || getSegmentTranslation(s, userLang) || '',
    }));
  }, [draftSubtitleNative_, filteredSentences, userLang, courseDuration_]);

  // Add grammar point to notes — link the note back to the sentence via
  // episodeId + sentenceIndex (episode-local, matching how other learning
  // modes record it) so the NotesPage deep-link can scroll here.
  const handleAddGrammarNote = useCallback(async (content: string, tags: string[], sentenceIndex: number) => {
    if (!courseId) return;
    try {
      await learningService.createNote({
        courseId,
        content,
        tags,
        ...(selectedEpisode?.id && { episodeId: selectedEpisode.id }),
        sentenceIndex,
      });
    } catch (err) {
      console.error('Failed to save grammar note:', err);
    }
  }, [courseId, selectedEpisode?.id]);

  // Deep-link scroll: open the analysis section, switch to sentences tab,
  // and scroll to the target card after the collapse/expand animation settles.
  useEffect(() => {
    if (targetLocalSentenceIdx === null || loading) return;
    if (!filteredContent.sentences?.length) return;
    if (!analysisOpen) setAnalysisOpen(true);
    if (activeTab !== 'sentences') setActiveTab('sentences');
    // Wait for the max-height transition (0.5s ease-in) before scrolling.
    // Manual scroll (not scrollIntoView) so we can offset for the sticky
    // header (56px) + content-tabs bar (~64px) that would otherwise cover
    // the top of the target sentence card.
    const timeoutId = window.setTimeout(() => {
      const el = document.getElementById(`sentence-${targetLocalSentenceIdx}`);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = window.scrollY + rect.top - 130;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 550);
    return () => window.clearTimeout(timeoutId);
  }, [targetLocalSentenceIdx, loading, filteredContent.sentences, activeTab, analysisOpen]);

  // Format a date string for human display in the active UI locale.
  // Uses Intl.DateTimeFormat with short month — short enough to sit inline
  // with the other meta badges across all 9 locales (CJK + Latin scripts).
  const formatDate = useCallback((dateStr: string): string => {
    try {
      return new Intl.DateTimeFormat(i18n.language || undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(dateStr));
    } catch {
      // Malformed input — return raw string rather than throwing in render.
      return dateStr;
    }
  }, [i18n.language]);

  // ── SEO / Open Graph / JSON-LD ──
  // Hook MUST be called unconditionally (React rules). On loading / error /
  // 404 we still emit a minimal title-only block so crawlers refreshing the
  // page don't see the global static <title>. JSON-LD `Course` is only
  // emitted once we have real course data — otherwise we'd publish bogus
  // schema. See Track 1E of doc/migration/seo-and-nextjs-plan.md.
  const courseForSeo = data?.course;
  const seoTitle = courseForSeo?.title || t('course:manage.untitledCourse');
  const seoDescription = (courseForSeo?.description || '').slice(0, 160);
  const seoOgImage = courseForSeo?.thumbnailUrl || courseForSeo?.coverImageUrl
    ? resolveMediaUrl(courseForSeo?.thumbnailUrl || courseForSeo?.coverImageUrl) || undefined
    : undefined;
  const seoJsonLd = courseForSeo
    ? buildCourseJsonLd({
        id: courseForSeo.id,
        title: courseForSeo.title,
        description: courseForSeo.description,
        image: seoOgImage,
        cefrLevel: courseForSeo.cefrLevel || courseForSeo.difficultyLevel,
        publishedAt: courseForSeo.publishedAt,
        updatedAt: courseForSeo.updatedAt,
        // Course entity has no priceUsd field on the client today — Loopy
        // gates content via subscription, not per-course pricing. Treat as
        // free at the schema level until per-course pricing lands.
        isFree: true,
      })
    : undefined;
  const seoTags = useSeoTags({
    title: seoTitle,
    description: seoDescription || seoTitle,
    canonical: courseId ? `/courses/${courseId}` : undefined,
    ogImage: seoOgImage,
    ogType: 'article',
    jsonLd: seoJsonLd,
    noindex: !courseForSeo,
  });

  if (loading) {
    return (
      <>
        {seoTags}
        <div className="course-detail-page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>{t('common:loading')}</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {seoTags}
        <div className="course-detail-page">
          <div className="error-container">
            <h2>{t('common:error')}</h2>
            <p>{error}</p>
            <button onClick={loadCourseData}>{t('common:retry')}</button>
            <Link to="/courses">{t('common:backToCourses')}</Link>
          </div>
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        {seoTags}
        <div className="course-detail-page">
          <div className="empty-container">
            <h2>{t('course:edit.notFound')}</h2>
            <Link to="/courses">{t('common:backToCourses')}</Link>
          </div>
        </div>
      </>
    );
  }

  const { course, sections, vocabulary, stats, subtitleUrlEn: draftSubtitleEn, subtitleUrlNative: draftSubtitleNative, draftAudioUrl } = data;
  const { sentences, comprehensiveVocabulary, phrases, audioStartTime, audioEndTime } = filteredContent;

  // Determine which vocabulary to use (comprehensive or legacy)
  const vocabToDisplay = comprehensiveVocabulary && comprehensiveVocabulary.length > 0
    ? comprehensiveVocabulary
    : null;

  // Calculate actual word count from sentences
  const actualWordCount = sentences?.reduce((total, sentence) => {
    const words = sentence.text?.split(/\s+/).filter((w: string) => w.length > 0) || [];
    return total + words.length;
  }, 0) || course.wordCount || 0;

  // Determine audio URL - prefer course audioUrl, fallback to draft audio
  const rawAudioUrl = course.audioUrl || draftAudioUrl;
  const audioUrl = resolveMediaUrl(rawAudioUrl) || undefined;

  // Determine subtitle URLs - use draft subtitle URLs from API response
  const subtitleUrlEn = resolveMediaUrl(draftSubtitleEn) || undefined;
  const subtitleUrlNative = resolveMediaUrl(draftSubtitleNative) || undefined;

  const hasSubtitles = subtitleUrlEn || subtitleUrlNative || fallbackCuesEn.length > 0 || fallbackCuesNative.length > 0;
  const hasSentences = sentences && sentences.length > 0;

  // Get annotations for a specific sentence index
  const getAnnotationsForSentence = (sentenceIndex: number): WordAnnotation[] => {
    const segmentId = segmentIdsByIndex[sentenceIndex];
    if (!segmentId) return [];
    return annotationsBySegment[segmentId] || [];
  };

  return (
    <>
      {seoTags}
      <div className="course-detail-page">
      {/* Header */}
      <header className="course-header">
        <div className="breadcrumb">
          <Link to={isCreatorMode ? '/creator/courses' : '/courses'}>
            {isCreatorMode ? t('course:manage.publishedCourses') : t('course:manage.courses')}
          </Link>
          <span>/</span>
          <span>{course.title || t('course:manage.untitledCourse')}</span>
        </div>

        {/* Integrated header: thumbnail + info side by side */}
        <div className="course-header-main">
          {/* Thumbnail - creator mode only */}
          {isCreatorMode && (
            <div className="thumbnail-section">
              <div className="thumbnail-container">
                {(course.thumbnailUrl || course.coverImageUrl) ? (
                  <img
                    src={resolveMediaUrl(course.thumbnailUrl || course.coverImageUrl)}
                    alt={course.title}
                    className="course-thumbnail-preview"
                  />
                ) : (
                  <div className="thumbnail-placeholder-large">
                    <span className="placeholder-icon">🖼️</span>
                  </div>
                )}
                {/* Regenerate overlay button */}
                <button
                  className={`btn-thumbnail-overlay ${generatingThumbnail ? 'generating' : ''}`}
                  onClick={() => setShowThumbnailConfirm(true)}
                  disabled={generatingThumbnail}
                  title={(course.thumbnailUrl || course.coverImageUrl) ? t('course:manage.regenerateThumbnail') : t('course:manage.generateThumbnail')}
                >
                  {generatingThumbnail ? (
                    <span className="spinner-small"></span>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  )}
                </button>
              </div>
              {thumbnailError && (
                <p className="thumbnail-error">{thumbnailError}</p>
              )}
            </div>
          )}

          {/* Management toolbar — on mobile it sits beside thumbnail via CSS grid */}
          {canEdit && (
            <div className="course-management-toolbar">
              {/* Status dropdown */}
              <div className="toolbar-control">
                <span className="toolbar-label">{t('course:manage.statusLabel')}</span>
                <div className="status-dropdown-wrapper" ref={statusDropdownRef}>
                  <button
                    className={`status-dropdown-trigger ${course.status}`}
                    onClick={() => setStatusDropdownOpen(v => !v)}
                    disabled={updatingStatus}
                  >
                    <span className={`status-dot ${course.status}`} />
                    <span>{course.status === 'published' ? t('course:manage.published') : t('course:manage.draft')}</span>
                    <svg className="dropdown-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {statusDropdownOpen && (
                    <div className="status-dropdown-menu">
                      <button
                        className={`status-dropdown-item ${course.status === 'published' ? 'active' : ''}`}
                        onClick={() => handleStatusChange('published')}
                      >
                        <span className="status-dot published" />
                        {t('course:manage.published')}
                      </button>
                      <button
                        className={`status-dropdown-item ${course.status === 'draft' ? 'active' : ''}`}
                        onClick={() => handleStatusChange('draft')}
                      >
                        <span className="status-dot draft" />
                        {t('course:manage.draft')}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Permission dropdown */}
              <div className="toolbar-control">
                <span className="toolbar-label">{t('course:manage.visibilityLabel')}</span>
                <div className="status-dropdown-wrapper" ref={permissionDropdownRef}>
                  <button
                    className={`status-dropdown-trigger ${course.permission || 'public'}`}
                    onClick={() => setPermissionDropdownOpen(v => !v)}
                    disabled={updatingPermission}
                  >
                    <span className={`permission-dot ${course.permission || 'public'}`} />
                    <span>{(course.permission || 'public') === 'public' ? t('course:manage.public') : t('course:manage.private')}</span>
                    <svg className="dropdown-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {permissionDropdownOpen && (
                    <div className="status-dropdown-menu">
                      <button
                        className={`status-dropdown-item ${(course.permission || 'public') === 'public' ? 'active' : ''}`}
                        onClick={() => handlePermissionChange('public')}
                      >
                        <span className="permission-dot public" />
                        {t('course:manage.public')}
                        <span className="dropdown-item-desc">{t('course:manage.publicDesc')}</span>
                      </button>
                      <button
                        className={`status-dropdown-item ${course.permission === 'private' ? 'active' : ''}`}
                        onClick={() => handlePermissionChange('private')}
                      >
                        <span className="permission-dot private" />
                        {t('course:manage.private')}
                        <span className="dropdown-item-desc">{t('course:manage.privateDesc')}</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Whitelist button — only when private */}
                {course.permission === 'private' && (
                  <button
                    className={`btn-whitelist-toggle ${whitelistOpen ? 'active' : ''}`}
                    onClick={() => setWhitelistOpen(v => !v)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    {t('course:manage.whitelist')}
                    <span className="whitelist-badge">{whitelistEntries.length}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Right side: title + meta */}
          <div className="course-header-info">
            {/* Title */}
            <div className="title-section">
              {canEdit && editingTitle ? (
                <div className="title-edit-form">
                  <label className="title-edit-label">{t('course:detail.englishTitle')}</label>
                  <input
                    type="text"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onKeyDown={handleTitleKeyDown}
                    autoFocus
                    className="title-input"
                    placeholder={t('course:manage.titlePlaceholder')}
                    disabled={savingTitle}
                  />
                  {nativeLang && (
                    <>
                      <label className="title-edit-label">{t('course:detail.nativeTitle')}</label>
                      <input
                        type="text"
                        value={nativeTitleValue}
                        onChange={(e) => setNativeTitleValue(e.target.value)}
                        onKeyDown={handleTitleKeyDown}
                        className="title-input title-input--native"
                        placeholder={t('course:detail.nativeTitlePlaceholder')}
                        disabled={savingTitle}
                      />
                    </>
                  )}
                  <div className="title-edit-actions">
                    <button
                      className="btn-title-save"
                      onClick={handleTitleSave}
                      disabled={savingTitle}
                    >
                      {savingTitle ? t('course:detail.saving') : t('course:detail.save')}
                    </button>
                    <button
                      className="btn-title-cancel"
                      onClick={() => {
                        setTitleValue(course.title || '');
                        setNativeTitleValue(nativeLang ? (course.titles?.[nativeLang] || '') : '');
                        setEditingTitle(false);
                      }}
                      disabled={savingTitle}
                    >
                      {t('course:detail.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="title-display">
                  <BilingualTitle
                    title={course.title}
                    titles={course.titles}
                    fallback={t('course:manage.untitledCourse')}
                    variant="header"
                    as="h1"
                  />
                  {canEdit && (
                    <button
                      className="btn-edit-title"
                      onClick={() => setEditingTitle(true)}
                      title={t('common:edit')}
                      aria-label={t('common:edit')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Meta badges */}
            {/* Meta badges — editable for creators */}
            {!editingMeta ? (
              <div className="course-meta-header">
                {(course.cefrLevel || course.difficultyLevel) && (
                  <Link
                    to={`/courses?level=${course.cefrLevel || course.difficultyLevel}`}
                    className="meta-badge difficulty clickable"
                  >
                    {course.cefrLevel || course.difficultyLevel}
                  </Link>
                )}
                {actualWordCount > 0 && <span className="meta-badge">{t('course:manage.wordsCount', { count: actualWordCount })}</span>}
                {course.durationSeconds != null && course.durationSeconds > 0 && (
                  <span className="meta-badge">{formatTime(course.durationSeconds)}</span>
                )}
                {(course as any).contentType && (
                  <Link
                    to={`/courses?contentType=${(course as any).contentType}`}
                    className="meta-badge content-type clickable"
                  >
                    {String(t(`course:filters.contentTypes.${(course as any).contentType}`, { defaultValue: (course as any).contentType }))}
                  </Link>
                )}
                {!canEdit && course.status !== 'published' && (
                  <span className={`meta-badge status ${course.status}`}>
                    {course.status}
                  </span>
                )}
                {(course as any).accent && (
                  <Link
                    to={`/courses?accent=${(course as any).accent}`}
                    className="meta-badge accent clickable"
                  >
                    {String(t(`course:filters.accents.${(course as any).accent}`, { defaultValue: (course as any).accent }))}
                  </Link>
                )}
                {course.publishedAt && (
                  <span
                    className="course-meta__published"
                    title={t('course:manage.publishedOn', { date: formatDate(course.publishedAt) })}
                  >
                    <time dateTime={course.publishedAt}>
                      {t('course:manage.publishedOn', { date: formatDate(course.publishedAt) })}
                    </time>
                    {course.updatedAt && course.updatedAt !== course.publishedAt && (
                      <span className="course-meta__updated">
                        {' · '}
                        <time dateTime={course.updatedAt}>
                          {t('course:manage.updatedOn', { date: formatDate(course.updatedAt) })}
                        </time>
                      </span>
                    )}
                  </span>
                )}
                {canEdit && (
                  <button className="btn-edit-meta" onClick={handleStartEditMeta} title={t('common:edit')}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              <div className="course-meta-editor">
                <div className="course-meta-editor__row">
                  <label className="course-meta-editor__label">{t('course:manage.cefrLevel')}</label>
                  <select className="course-meta-editor__select" value={metaCefrLevel} onChange={e => setMetaCefrLevel(e.target.value)}>
                    <option value="">—</option>
                    {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(lvl => (
                      <option key={lvl} value={lvl}>{lvl}</option>
                    ))}
                  </select>
                </div>
                <div className="course-meta-editor__row">
                  <label className="course-meta-editor__label">{t('course:manage.contentType')}</label>
                  <select className="course-meta-editor__select" value={metaContentType} onChange={e => setMetaContentType(e.target.value)}>
                    <option value="">—</option>
                    {['dialogue', 'article', 'news', 'speech', 'advertisement', 'story'].map(ct => (
                      <option key={ct} value={ct}>{String(t(`course:filters.contentTypes.${ct}`, { defaultValue: ct }))}</option>
                    ))}
                  </select>
                </div>
                <div className="course-meta-editor__row">
                  <label className="course-meta-editor__label">{t('course:manage.accent')}</label>
                  <select className="course-meta-editor__select" value={metaAccent} onChange={e => setMetaAccent(e.target.value)}>
                    <option value="">—</option>
                    {['american', 'british', 'australian', 'indian', 'southern', 'mixed', 'neutral'].map(a => (
                      <option key={a} value={a}>{String(t(`course:filters.accents.${a}`, { defaultValue: a }))}</option>
                    ))}
                  </select>
                </div>
                <div className="course-meta-editor__row course-meta-editor__row--tags">
                  <label className="course-meta-editor__label">{t('course:manage.tags', { defaultValue: 'Tags' })}</label>
                  <TagChipInput
                    value={metaTags}
                    onChange={setMetaTags}
                    suggestions={tagSuggestions}
                    maxTags={5}
                    placeholder={t('course:manage.tagsPlaceholder', { defaultValue: 'Type or pick from suggestions' })}
                  />
                </div>
                <div className="course-meta-editor__actions">
                  <button className="btn-meta-save" onClick={handleSaveMeta} disabled={savingMeta}>
                    {savingMeta ? t('common:saving', { defaultValue: 'Saving...' }) : t('common:save', { defaultValue: 'Save' })}
                  </button>
                  <button className="btn-meta-cancel" onClick={() => setEditingMeta(false)}>
                    {t('common:cancel', { defaultValue: 'Cancel' })}
                  </button>
                </div>
              </div>
            )}

            {/* Topics + Tags */}
            {((course.topics && course.topics.length > 0) || (course.tags && course.tags.length > 0)) && (
              <div className="course-topics-tags">
                {course.topics?.map((topic) => (
                  <span key={topic.code} className="topic-badge">
                    {topic.labels?.[i18n.language] || topic.labels?.['en'] || topic.code}
                  </span>
                ))}
                {course.tags?.map((tag) => (
                  <Link
                    key={`content-tag-${tag}`}
                    to={`/courses?q=${encodeURIComponent(tag)}`}
                    className="badge content-tag-badge clickable"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}

          </div>{/* end course-header-info */}
        </div>{/* end course-header-main */}

        {/* Whitelist panel — collapsible */}
        {canEdit && course.permission === 'private' && whitelistOpen && (
          <div className="whitelist-section">
            <div className="whitelist-input-row">
              <input
                type="text"
                className="whitelist-input"
                placeholder={t('course:manage.whitelistPlaceholder')}
                value={whitelistInput}
                onChange={e => setWhitelistInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddWhitelistEmails(); } }}
              />
              <button className="btn-add-whitelist" onClick={handleAddWhitelistEmails} disabled={!whitelistInput.trim()}>
                {t('course:manage.whitelistAdd')}
              </button>
            </div>
            {whitelistLoading ? (
              <p className="whitelist-loading">{t('course:manage.whitelistLoading')}</p>
            ) : whitelistEntries.length > 0 ? (
              <div className="whitelist-tags">
                {whitelistEntries.map(entry => (
                  <span key={entry.id} className="whitelist-tag">
                    {entry.email}
                    <button className="whitelist-tag-remove" onClick={() => handleRemoveWhitelistEmail(entry.email)}>&times;</button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="whitelist-empty">{t('course:manage.whitelistEmpty')}</p>
            )}
          </div>
        )}

        {/* Unpublish confirmation modal */}
        {showUnpublishConfirm && (
          <div className="modal-overlay" onClick={() => setShowUnpublishConfirm(false)}>
            <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
              <h3>{t('course:manage.unpublishTitle')}</h3>
              <p>{t('course:manage.unpublishWarning')}</p>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowUnpublishConfirm(false)}>
                  {t('common:cancel')}
                </button>
                <button className="btn-danger" onClick={handleConfirmUnpublish}>
                  {t('course:manage.confirmUnpublish')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Make private confirmation modal */}
        {showPrivateConfirm && (
          <div className="modal-overlay" onClick={() => setShowPrivateConfirm(false)}>
            <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
              <h3>{t('course:manage.makePrivateTitle')}</h3>
              <p>{t('course:manage.makePrivateWarning')}</p>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowPrivateConfirm(false)}>
                  {t('common:cancel')}
                </button>
                <button className="btn-warning" onClick={() => handleConfirmPermission('private')}>
                  {t('course:manage.confirmMakePrivate')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Make public confirmation modal */}
        {showPublicConfirm && (
          <div className="modal-overlay" onClick={() => setShowPublicConfirm(false)}>
            <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
              <h3>{t('course:manage.makePublicTitle')}</h3>
              <p>{t('course:manage.makePublicWarning')}</p>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowPublicConfirm(false)}>
                  {t('common:cancel')}
                </button>
                <button className="btn-primary" onClick={() => handleConfirmPermission('public')}>
                  {t('course:manage.confirmMakePublic')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Thumbnail regeneration confirmation modal */}
        {showThumbnailConfirm && (
          <div className="modal-overlay" onClick={() => setShowThumbnailConfirm(false)}>
            <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
              <h3>{(course.thumbnailUrl || course.coverImageUrl) ? t('course:manage.regenerateThumbnailTitle') : t('course:manage.generateThumbnailTitle')}</h3>
              <p>{(course.thumbnailUrl || course.coverImageUrl) ? t('course:manage.regenerateThumbnailWarning') : t('course:manage.generateThumbnailDesc')}</p>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowThumbnailConfirm(false)}>
                  {t('common:cancel')}
                </button>
                <button className="btn-primary" onClick={() => { setShowThumbnailConfirm(false); handleGenerateThumbnail(); }}>
                  {(course.thumbnailUrl || course.coverImageUrl) ? t('course:manage.regenerateThumbnail') : t('course:manage.generateThumbnail')}
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Success/Error Messages */}
      {successMessage && <div className="success-banner">{successMessage}</div>}

      {/* Audio Player Section — "聆聽課程" */}
      {audioUrl && (
        <div className="audio-preview-section">
          <div className="section-header">
            <h3>{t('course:manage.listenCourse')}</h3>
          </div>

          <UnifiedAudioPlayer
            audioUrl={audioUrl}
            subtitleUrlEn={subtitleUrlEn}
            subtitleUrlNative={subtitleUrlNative}
            fallbackCuesEn={fallbackCuesEn}
            fallbackCuesNative={fallbackCuesNative}
            startTime={epAudioStart}
            endTime={epAudioEnd}
          />
        </div>
      )}

      {/* Learning Modes — collapsible */}
      {!isCreatorMode && (
        <div className={`collapsible-section learning-entry-section ${learningModesOpen ? 'open' : ''}`}>
          <button className="collapsible-section__toggle" onClick={() => setLearningModesOpen(v => !v)}>
            <h3>{t('course:manage.learningModes')}</h3>
            <svg className="collapsible-section__chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div className="collapsible-section__body">
            {/* Multi-episode: episode selector or episode detail */}
            {isMultiEpisode && sections && (
              <>
                {selectedEp === null ? (
                  <>
                    <p className="learning-entry-description">{t('course:detail.selectEpisode')}</p>
                    <EpisodeCardGrid
                      sections={sections}
                      courseId={courseId!}
                      navigate={navigate}
                    />
                  </>
                ) : (
                  <>
                    {/* Episode header with back + title */}
                    <div className="episode-selected-header">
                      <button
                        className="episode-back-btn"
                        onClick={() => navigate(`/courses/${courseId}`)}
                        aria-label={t('course:detail.allEpisodes')}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 12L6 8l4-4" />
                        </svg>
                        {t('course:detail.allEpisodes')}
                      </button>
                      <h3 className="episode-selected-title">
                        <span className="episode-selected-num" style={{ background: EPISODE_COLORS[selectedEp % EPISODE_COLORS.length] }}>
                          {selectedEp + 1}
                        </span>
                        {selectedEpisode?.title}
                      </h3>
                    </div>

                    {/* Compact pill nav for quick switching */}
                    <EpisodePillNav
                      sections={sections}
                      courseId={courseId!}
                      navigate={navigate}
                      selectedEp={selectedEp}
                    />

                    {/* Episode stats summary */}
                    <div className="episode-stats">
                      <span>{sentences?.length || 0} {t('course:detail.sentencesUnit')}</span>
                      <span>{vocabToDisplay?.length || 0} {t('course:manage.vocabularyLabel')}</span>
                      <span>{phrases?.length || 0} {t('course:detail.phrasesUnit', { defaultValue: 'phrases' })}</span>
                      {selectedEpisode?.durationSeconds && (
                        <span>{formatEpDuration(selectedEpisode.durationSeconds)}</span>
                      )}
                    </div>

                    {/* Learning mode buttons */}
                    <div className="learning-mode-buttons">
                      <Link to={`/courses/${courseId}/comprehension?ep=${selectedEp}`} className="learning-mode-btn comprehension" onClick={(e) => handleModeClick(e, `/courses/${courseId}/comprehension?ep=${selectedEp}`)}>
                        <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4.5h5.5c1.1 0 2 .9 2 2v10a1.5 1.5 0 0 0-1.5-1.5H3V4.5z" /><path d="M17 4.5h-5.5c-1.1 0-2 .9-2 2v10a1.5 1.5 0 0 1 1.5-1.5H17V4.5z" /></svg></span>
                        <span className="mode-name">{t('course:manage.comprehension')}</span>
                        {renderProgressBadge(progressSummary?.comprehension)}
                      </Link>
                      <Link to={`/courses/${courseId}/vocabulary?ep=${selectedEp}`} className="learning-mode-btn vocabulary" onClick={(e) => handleModeClick(e, `/courses/${courseId}/vocabulary?ep=${selectedEp}`)}>
                        <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="11" height="13" rx="1.5" /><path d="M6 4V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-1" /><path d="M6 8.5h5" /><path d="M6 11.5h3" /></svg></span>
                        <span className="mode-name">{t('course:manage.vocabularyMode')}</span>
                        {renderProgressBadge(progressSummary?.vocabulary)}
                      </Link>
                      <Link to={`/courses/${courseId}/shadowing?ep=${selectedEp}`} className="learning-mode-btn shadowing" onClick={(e) => handleModeClick(e, `/courses/${courseId}/shadowing?ep=${selectedEp}`)}>
                        <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3v10" /><rect x="8" y="3" width="4" height="7" rx="2" /><path d="M6 10a4 4 0 0 0 8 0" /><path d="M10 13v4" /><path d="M7 17h6" /></svg></span>
                        <span className="mode-name">{t('course:manage.shadowingMode')}</span>
                        {renderProgressBadge(progressSummary?.shadowing)}
                      </Link>
                      <Link to={`/courses/${courseId}/quiz?ep=${selectedEp}`} className="learning-mode-btn quiz" onClick={(e) => handleModeClick(e, `/courses/${courseId}/quiz?ep=${selectedEp}`)}>
                        <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2.5" width="12" height="15" rx="1.5" /><path d="M7 6.5h6" /><path d="M7 9.5h6" /><path d="M7 12.5h4" /></svg></span>
                        <span className="mode-name">{t('course:manage.quizMode')}</span>
                        {renderProgressBadge(progressSummary?.quiz)}
                      </Link>
                      <Link to={`/courses/${courseId}/dictation?ep=${selectedEp}`} className="learning-mode-btn dictation" onClick={(e) => handleModeClick(e, `/courses/${courseId}/dictation?ep=${selectedEp}`)}>
                        <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z" /><path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z" /></svg></span>
                        <span className="mode-name">{t('course:manage.dictationMode')}</span>
                        {renderProgressBadge(progressSummary?.dictation)}
                      </Link>
                      <Link to={`/courses/${courseId}/quest?ep=${selectedEp}`} className="learning-mode-btn quest" onClick={(e) => handleModeClick(e, `/courses/${courseId}/quest?ep=${selectedEp}`)}>
                        <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2.5H5.5A1.5 1.5 0 0 0 4 4v12a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 16 16V4.5L14 2.5z" /><path d="M14 2.5V5h2.5" /><path d="M12 11l-3.5 3.5L7 13" /><path d="M7 8h6" /></svg></span>
                        <span className="mode-name">{t('course:manage.questMode')}</span>
                      </Link>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Single-episode: show learning mode buttons directly */}
            {!isMultiEpisode && (
              <>
                <div className="learning-mode-buttons">
                  <Link to={`/courses/${courseId}/comprehension`} className="learning-mode-btn comprehension" onClick={(e) => handleModeClick(e, `/courses/${courseId}/comprehension`)}>
                    <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4.5h5.5c1.1 0 2 .9 2 2v10a1.5 1.5 0 0 0-1.5-1.5H3V4.5z" /><path d="M17 4.5h-5.5c-1.1 0-2 .9-2 2v10a1.5 1.5 0 0 1 1.5-1.5H17V4.5z" /></svg></span>
                    <span className="mode-name">{t('course:manage.comprehension')}</span>
                    {renderProgressBadge(progressSummary?.comprehension)}
                  </Link>
                  <Link to={`/courses/${courseId}/vocabulary`} className="learning-mode-btn vocabulary" onClick={(e) => handleModeClick(e, `/courses/${courseId}/vocabulary`)}>
                    <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="11" height="13" rx="1.5" /><path d="M6 4V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-1" /><path d="M6 8.5h5" /><path d="M6 11.5h3" /></svg></span>
                    <span className="mode-name">{t('course:manage.vocabularyMode')}</span>
                    {renderProgressBadge(progressSummary?.vocabulary)}
                  </Link>
                  <Link to={`/courses/${courseId}/shadowing`} className="learning-mode-btn shadowing" onClick={(e) => handleModeClick(e, `/courses/${courseId}/shadowing`)}>
                    <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3v10" /><rect x="8" y="3" width="4" height="7" rx="2" /><path d="M6 10a4 4 0 0 0 8 0" /><path d="M10 13v4" /><path d="M7 17h6" /></svg></span>
                    <span className="mode-name">{t('course:manage.shadowingMode')}</span>
                    {renderProgressBadge(progressSummary?.shadowing)}
                  </Link>
                  <Link to={`/courses/${courseId}/quiz`} className="learning-mode-btn quiz" onClick={(e) => handleModeClick(e, `/courses/${courseId}/quiz`)}>
                    <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2.5" width="12" height="15" rx="1.5" /><path d="M7 6.5h6" /><path d="M7 9.5h6" /><path d="M7 12.5h4" /></svg></span>
                    <span className="mode-name">{t('course:manage.quizMode')}</span>
                    {renderProgressBadge(progressSummary?.quiz)}
                  </Link>
                  <Link to={`/courses/${courseId}/dictation`} className="learning-mode-btn dictation" onClick={(e) => handleModeClick(e, `/courses/${courseId}/dictation`)}>
                    <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z" /><path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z" /></svg></span>
                    <span className="mode-name">{t('course:manage.dictationMode')}</span>
                    {renderProgressBadge(progressSummary?.dictation)}
                  </Link>
                  <Link to={`/courses/${courseId}/quest`} className="learning-mode-btn quest" onClick={(e) => handleModeClick(e, `/courses/${courseId}/quest`)}>
                    <span className="mode-icon"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2.5H5.5A1.5 1.5 0 0 0 4 4v12a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 16 16V4.5L14 2.5z" /><path d="M14 2.5V5h2.5" /><path d="M12 11l-3.5 3.5L7 13" /><path d="M7 8h6" /></svg></span>
                    <span className="mode-name">{t('course:manage.questMode')}</span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sentence Analysis — collapsible, contains tabs + content */}
      <div className={`collapsible-section analysis-section ${analysisOpen ? 'open' : ''}`}>
        <button className="collapsible-section__toggle" onClick={() => setAnalysisOpen(v => !v)}>
          <h3>{t('course:manage.sentenceAnalysis')}</h3>
          <svg className="collapsible-section__chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div className="collapsible-section__body">
          {/* Content Tabs */}
          <div className="content-tabs">
            <button
              className={`tab-btn ${activeTab === 'sentences' ? 'active' : ''}`}
              onClick={() => setActiveTab('sentences')}
              disabled={!hasSentences}
            >
              {t('course:manage.sentencesTab', { count: hasSentences ? sentences?.length ?? 0 : 0 })}
            </button>
            <button
              className={`tab-btn ${activeTab === 'vocabulary' ? 'active' : ''}`}
              onClick={() => setActiveTab('vocabulary')}
            >
              {t('course:manage.vocabularyTab', { count: vocabToDisplay ? vocabToDisplay.length : vocabulary.length })}
            </button>
            <button
              className={`tab-btn ${activeTab === 'phrases' ? 'active' : ''}`}
              onClick={() => setActiveTab('phrases')}
              disabled={!phrases || phrases.length === 0}
            >
              {t('course:manage.phrasesTab', { count: phrases?.length || 0 })}
            </button>
            {canEdit && (
              <button
                className={`tab-btn ${activeTab === 'writing' ? 'active' : ''}`}
                onClick={() => setActiveTab('writing')}
              >
                {t('course:manage.writingPracticeTab')}
              </button>
            )}
          </div>

          {/* Sentences Tab */}
          {activeTab === 'sentences' && hasSentences && (
            <div className="learning-content-section">
              <div className="sentences-toolbar">
                <button
                  className="btn-toggle-all-sentences"
                  onClick={() => {
                    setSentencesCollapseState(prev => !prev);
                    setSentencesCollapseKey(prev => prev + 1);
                  }}
                >
                  {sentencesCollapseState ? t('course:manage.expandAll') : t('course:manage.collapseAll')}
                </button>
              </div>
              <div className="sentences-container">
                {sentences?.map((sentence: AnnotatedSentence, index: number) => {
                  const isTarget = targetLocalSentenceIdx === index;
                  return (
                    <div key={`sentence-wrap-${index}`} id={`sentence-${index}`}>
                      <SentenceDisplay
                        key={`sentence-${sentencesCollapseKey}-${targetLocalSentenceIdx ?? 'none'}-${index}-${sentence.text?.substring(0, 20)?.replace(/\s/g, '-') || 'empty'}`}
                        sentence={sentence}
                        index={index}
                        vocabulary={vocabToDisplay || []}
                        onEdit={canEdit ? setEditingSentenceIndex : undefined}
                        annotations={getAnnotationsForSentence(index)}
                        defaultCollapsed={isTarget ? false : sentencesCollapseState}
                        onAddGrammarNote={handleAddGrammarNote}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

      {/* Vocabulary Tab - Comprehensive Vocabulary */}
      {activeTab === 'vocabulary' && (
        <div className="vocabulary-section">
          <div className="vocabulary-header">
            <h3>{t('course:manage.vocabularyList', { count: vocabToDisplay ? vocabToDisplay.length : vocabulary.length })}</h3>
            <p>
              {t('course:manage.vocabularyListDesc')}
            </p>
          </div>

          {vocabToDisplay ? (
            // Comprehensive vocabulary cards
            <div className="vocabulary-cards">
              {vocabToDisplay.map((vocab, idx) => (
                <div
                  key={`vocab-${idx}-${vocab.word}`}
                  className="vocab-card clickable"
                  onClick={() => setSelectedVocab(vocab)}
                  title={t('course:manage.clickForDetails')}
                >
                  <div className="vocab-card-header">
                    <span className="vocab-word">{vocab.word}</span>
                    <span className="vocab-pos">{vocab.pos ? t(`creator:workflow.contentEditor.pos${vocab.pos.charAt(0).toUpperCase()}${vocab.pos.slice(1).toLowerCase()}`, { defaultValue: vocab.pos }) : ''}</span>
                  </div>
                  {vocab.pronunciation && (
                    <div className="vocab-pronunciation">
                      /{convertPhonetic(vocab.pronunciation, phoneticSystem)}/
                    </div>
                  )}
                  <div className="vocab-definitions">
                    <p className="vocab-def-en">{vocab.general_definition_en}</p>
                    <p className="vocab-def-zh">{vocab.general_definition}</p>
                    {vocab.definition_in_context && (
                      <p className="vocab-context">
                        <strong>{t('course:manage.inContext')}</strong> {vocab.definition_in_context}
                      </p>
                    )}
                  </div>
                  {vocab.example_sentences && vocab.example_sentences.length > 0 && (
                    <div className="vocab-examples">
                      <strong>{t('course:manage.examples')}</strong>
                      {vocab.example_sentences.slice(0, 2).map((ex, exIdx) => (
                        <div key={exIdx} className="vocab-example">
                          <p className="example-en">{ex.sentence_en}</p>
                          <p className="example-zh">{ex.sentence_native}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {vocab.synonyms && vocab.synonyms.length > 0 && (
                    <div className="vocab-related">
                      <span className="related-label">{t('course:manage.synonyms')}</span>
                      {vocab.synonyms.slice(0, 3).map((syn, sIdx) => (
                        <span key={sIdx} className="related-tag synonym">{syn}</span>
                      ))}
                    </div>
                  )}
                  {vocab.collocations && vocab.collocations.length > 0 && (
                    <div className="vocab-related">
                      <span className="related-label">{t('course:manage.collocations')}</span>
                      {vocab.collocations.slice(0, 3).map((col, cIdx) => (
                        <span key={cIdx} className="related-tag collocation">{col}</span>
                      ))}
                    </div>
                  )}
                  {vocab.vocabulary_quiz && (
                    <div className="vocab-quiz-indicator">
                      {t('course:manage.hasQuiz')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // Legacy vocabulary grid
            <div className="vocabulary-grid">
              {vocabulary.map((vocab) => (
                <div key={vocab.id} className="vocab-card-legacy">
                  <div className="vocab-header">
                    <span className="vocab-word">{vocab.word}</span>
                    {vocab.partOfSpeech && <span className="vocab-pos">{t(`creator:workflow.contentEditor.pos${vocab.partOfSpeech.charAt(0).toUpperCase()}${vocab.partOfSpeech.slice(1).toLowerCase()}`, { defaultValue: vocab.partOfSpeech })}</span>}
                  </div>
                  {vocab.pronunciation && (
                    <div className="vocab-pronunciation">/{convertPhonetic(vocab.pronunciation, phoneticSystem)}/</div>
                  )}
                  {vocab.definition && <div className="vocab-definition-en">{vocab.definition}</div>}
                  {vocab.definitionNative && (
                    <div className="vocab-definition-zh">{vocab.definitionNative}</div>
                  )}
                  {vocab.examples && vocab.examples.length > 0 && (
                    <div className="vocab-examples-legacy">
                      <strong>{t('course:manage.examples')}</strong>
                      <ul>
                        {vocab.examples.slice(0, 2).map((ex, i) => (
                          <li key={i}>{ex}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Phrases Tab */}
      {activeTab === 'phrases' && phrases && phrases.length > 0 && (
        <div className="phrases-section">
          <div className="phrases-header">
            <h3>{t('course:manage.phrasesList', { count: phrases.length })}</h3>
            <p>{t('course:manage.phrasesListDesc')}</p>
          </div>
          <div className="phrases-grid">
            {phrases.map((phrase, idx) => (
              <div key={`phrase-${idx}-${phrase.text?.substring(0, 15)?.replace(/\s/g, '-') || 'empty'}`} className="phrase-card">
                <div className="phrase-header">
                  <span className="phrase-text">{phrase.text}</span>
                  <div className="phrase-header-right">
                    <span className={`phrase-type ${phrase.type}`}>{t(`creator:workflow.phraseEditModal.phraseTypes.${phrase.type}`, { defaultValue: phrase.type?.replace('_', ' ') })}</span>
                    {canEdit && (
                      <button
                        type="button"
                        className="btn-edit-phrase"
                        onClick={() => setEditingPhraseIndex(idx)}
                        title={t('course:manage.editPhrase')}
                        aria-label={t('course:manage.editPhrase')}
                      >
                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <div className="phrase-definitions">
                  <p className="phrase-def-en">{phrase.definition_en}</p>
                  <p className="phrase-def-zh">{getTranslation(phrase.definition_natives, userLang, phrase.definition_native)}</p>
                </div>
                {(phrase.explanation_native || phrase.explanation_natives) && (
                  <div className="phrase-explanation">
                    <strong>{t('course:manage.explanation')}</strong> {getTranslation(phrase.explanation_natives, userLang, phrase.explanation_native || '')}
                  </div>
                )}
                {phrase.usage_example && (
                  <div className="phrase-usage">
                    <strong>{t('course:manage.example')}</strong> {phrase.usage_example}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Writing Practice Tab */}
      {activeTab === 'writing' && canEdit && courseId && (
        <div className="writing-practice-section">
          <div className="writing-practice-intro">
            <p>{t('course:manage.writingPracticeDesc')}</p>
            <Link
              to={`/creator/courses/${courseId}/quest-questions`}
              className="btn-writing-practice"
            >
              {t('course:manage.editWritingPractice')}
            </Link>
          </div>
        </div>
      )}

        </div>{/* end collapsible-section__body */}
      </div>{/* end collapsible-section analysis-section */}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
        <Link
          to={isCreatorMode ? '/creator/courses' : '/courses'}
          style={{
            padding: '10px 20px',
            background: '#f1f5f9',
            color: '#475569',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '1.5',
            cursor: 'pointer',
          }}
        >
          {t('common:backToCourses')}
        </Link>
        {canEdit && (
          <button
            type="button"
            style={{
              padding: '10px 20px',
              background: '#dc3545',
              color: 'white',
              border: '1px solid #dc3545',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              fontWeight: 500,
              lineHeight: '1.5',
              cursor: 'pointer',
            }}
            onClick={() => setShowDeleteModal(true)}
          >
            {t('course:manage.deleteCourse')}
          </button>
        )}
        {!isCreatorMode && isEnrolled && (
          <button
            type="button"
            style={{
              padding: '10px 20px',
              background: '#dc3545',
              color: 'white',
              border: '1px solid #dc3545',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              fontWeight: 500,
              lineHeight: '1.5',
              cursor: 'pointer',
            }}
            onClick={() => setShowUnenrollModal(true)}
          >
            {t('course:manage.unenroll')}
          </button>
        )}
        {!isCreatorMode && isEnrolled === false && (
          <button
            type="button"
            style={{
              padding: '10px 20px',
              background: 'var(--color-primary, #667eea)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              fontWeight: 500,
              lineHeight: '1.5',
              cursor: 'pointer',
            }}
            onClick={() => setShowEnrollModal(true)}
          >
            {t('course:courseList.enroll')}
          </button>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('course:manage.deleteCourse')}</h3>
            <div className="modal-warning">
              {t('course:manage.deleteWarning')}
            </div>
            <p className="modal-instruction">
              {t('course:manage.deleteInstruction')}
            </p>
            <input
              type="text"
              className="delete-confirm-input"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="delete"
              autoFocus
              disabled={isDeleting}
            />
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                disabled={isDeleting}
              >
                {t('common:cancel')}
              </button>
              <button
                className="btn-confirm-delete"
                onClick={handleDeleteCourse}
                disabled={deleteConfirmText !== 'delete' || isDeleting}
              >
                {isDeleting ? t('course:manage.deleting') : t('course:manage.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unenroll Confirmation Modal */}
      {showUnenrollModal && (
        <div className="modal-overlay" onClick={() => !isUnenrolling && setShowUnenrollModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('course:manage.unenroll')}</h3>
            <div className="modal-warning">
              {t('course:manage.unenrollWarning')}
            </div>
            <p className="modal-instruction">
              {t('course:manage.unenrollInstruction')}
            </p>
            <input
              type="text"
              className="delete-confirm-input"
              value={unenrollConfirmText}
              onChange={(e) => setUnenrollConfirmText(e.target.value)}
              placeholder="cancel"
              autoFocus
              disabled={isUnenrolling}
            />
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowUnenrollModal(false);
                  setUnenrollConfirmText('');
                }}
                disabled={isUnenrolling}
              >
                {t('common:cancel')}
              </button>
              <button
                className="btn-confirm-delete"
                onClick={handleUnenroll}
                disabled={unenrollConfirmText !== 'cancel' || isUnenrolling}
              >
                {isUnenrolling ? t('course:manage.unenrolling') : t('course:manage.confirmUnenroll')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Confirmation Modal */}
      {showEnrollModal && (
        <div className="modal-overlay" onClick={() => !isEnrolling && setShowEnrollModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('course:enrollment.enrollToLearn')}</h3>
            <p className="modal-instruction">
              {t('course:enrollment.enrollToLearnDesc')}
            </p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowEnrollModal(false);
                  setPendingModePath(null);
                }}
                disabled={isEnrolling}
              >
                {t('common:cancel')}
              </button>
              <button
                className="btn-confirm-enroll"
                onClick={handleEnroll}
                disabled={isEnrolling}
              >
                {isEnrolling ? t('course:courseList.enrolling') : t('course:courseList.enroll')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Required Modal */}
      {showSubscriptionModal && (
        <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('pricing:gate.subscriptionRequired')}</h3>
            <p className="modal-instruction">
              {t('pricing:gate.subscriptionDescription')}
            </p>
            <ul style={{ textAlign: 'left', margin: '12px 0', paddingLeft: '20px', fontSize: '14px', color: '#555', lineHeight: '1.8' }}>
              <li>{t('pricing:gate.benefit1')}</li>
              <li>{t('pricing:gate.benefit2')}</li>
              <li>{t('pricing:gate.benefit3')}</li>
            </ul>
            <p style={{ fontSize: '13px', color: '#888', margin: '8px 0 16px' }}>
              {t('pricing:gate.priceHint')}
            </p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => { setShowSubscriptionModal(false); navigate(-1); }}
              >
                {t('pricing:gate.goBack')}
              </button>
              <button
                className="btn-confirm-enroll"
                onClick={() => { setShowSubscriptionModal(false); navigate('/pricing'); }}
              >
                {t('pricing:gate.subscribe')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vocabulary Modal */}
      {selectedVocab && (
        <VocabularyModal
          vocabulary={selectedVocab}
          onClose={() => setSelectedVocab(null)}
        />
      )}

      {/* Sentence Edit Modal - only shown in creator mode */}
      {editingSentenceIndex !== null && sentences && sentences[editingSentenceIndex] && (
        <SentenceEditModal
          sentence={sentences[editingSentenceIndex]}
          sentenceIndex={editingSentenceIndex}
          onSave={handleSentenceSave}
          onClose={() => setEditingSentenceIndex(null)}
          isLoading={savingSentence}
        />
      )}

      {/* Phrase Edit Modal - only shown in creator mode */}
      {editingPhraseIndex !== null && phrases && phrases[editingPhraseIndex] && (
        <PhraseEditModal
          phrase={phrases[editingPhraseIndex]}
          phraseIndex={editingPhraseIndex}
          onSave={handlePhraseSave}
          onClose={() => setEditingPhraseIndex(null)}
          isLoading={savingPhrase}
        />
      )}
      </div>
    </>
  );
};

export default CourseDetailPage;
